import { Form } from "@inertiajs/react"
import { Loader2, Pencil } from "lucide-react"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { OnboardingCountrySelect } from "@/components/onboarding/onboarding-country-select"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Textarea } from "@/components/ui/textarea"
import { useOnline } from "@/hooks/use-online"
import { toMessages } from "@/lib/form-errors"
import { candidatePath } from "@/routes"
import type { CandidateDetail } from "@/types/candidates"

interface EditCandidateSheetProps {
  /** The candidate whose profile is being edited. Supplies the prefill. */
  candidate: CandidateDetail
}

/**
 * EditCandidateSheet — OKS-911 candidate profile edit surface.
 *
 * A right-side `Sheet` (mirrors the OKS-629 `AddCandidateSheet` shape)
 * whose trigger is the profile card's "Edit profile" button. Wraps an
 * Inertia `<Form>` **PATCH** to `candidatePath(id)` widening the six
 * create-form profile fields (`tiktok_url`, `display_name`,
 * `approx_followers`, `bio`, `country`, `city`), each **prefilled** with
 * the candidate's current value. Notes / tags / email are NOT here — they
 * keep their own always-editable inline editors on the card.
 *
 * **Rendered only when the viewer `can_edit_profile`** (role AND status ∈
 * {`new`, `reviewed`}). On a decided candidate the parent renders nothing,
 * so this component never mounts (FE half of the defense-in-depth gate; the
 * controller 404 on a crafted profile PATCH is the server half).
 *
 * **Open state is LOCAL** (`useState`), not URL-driven (LSE call, UX Spec
 * §10): the Sheet opens from a button on the already-loaded detail page
 * with all data in props, so there's no share/refresh-survival benefit to a
 * `?edit` param — and local state sidesteps the OKS-662/667 URL-close race
 * machinery. Because the `<SheetTrigger>` is captured, Base UI returns focus
 * to the Edit button on every close path automatically (no `finalFocus`).
 *
 * **Wire shape (locked with qa-lead, OKS-911 TRD):** every input's `name`
 * is scoped under `candidate[*]` so Rails reads
 * `params[:candidate][:tiktok_url]` etc. The TikTok URL field is prefilled
 * with the canonical `tiktok_handle` (a valid input that round-trips through
 * `Candidate.normalize_handle` unchanged; the raw stored URL is not on the
 * detail wire and a bare handle is the least-surprising editable value).
 *
 * **Errors** — per-field `<InputError>` from the `<Form>` render-prop
 * `errors`, which are the SOLE error source. The brand-scoped TikTok
 * uniqueness collision arrives as `errors.tiktok_url` (`:taken`). On a
 * validation failure the controller `redirect_back`s at 302 (NOT 303) so
 * the flashed errors survive; `<Form>`'s `preserveState` default keeps the
 * component mounted with its typed values + errors (OKS-711/662 caveat — do
 * NOT read the page error bag here).
 *
 * **Success** — the controller redirects (303) to the clean detail URL with
 * a `Profile updated` flash; `onSuccess` closes the Sheet.
 *
 * Locked QA testid contract (frozen bilaterally with qa-lead):
 *   - `edit-candidate-trigger` — the Edit profile button (SheetTrigger)
 *   - `edit-candidate-sheet` — SheetContent
 *   - `edit-candidate-form` — Form element
 *   - `candidate-edit-input-{tiktok-url, display-name, approx-followers,
 *      bio, country, city}`
 *   - `candidate-edit-error-{field}` — per-field error
 *   - `edit-candidate-submit`, `edit-candidate-cancel`,
 *     `edit-candidate-offline-banner`
 */
export function EditCandidateSheet({ candidate }: EditCandidateSheetProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [open, setOpen] = useState(false)

  // Mirror processing into a ref so the Sheet's `onOpenChange` (outside the
  // Form scope) can swallow an in-flight dismissal without a stale closure
  // (UX Spec §4 — mirrors the add-Sheet swallow-with-feedback).
  const submittingRef = useRef(false)

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        // Block Escape / outside-click dismissal while a submit is in flight;
        // the in-flight feedback is the disabled spinner Save button. Once
        // the submit resolves the guard is inactive, so a fresh dismissal
        // closes normally (including the validation-error case). The server
        // redirect closes via `onSuccess` → `setOpen(false)`, not through
        // this handler.
        if (!next && submittingRef.current) return
        setOpen(next)
      }}
    >
      <SheetTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="edit-candidate-trigger"
            className="max-sm:h-11"
          >
            <Pencil className="size-4" aria-hidden="true" />
            {t("candidates.detail.profile.edit_button")}
          </Button>
        }
      />
      <SheetContent
        side="right"
        className="overflow-y-auto sm:max-w-lg"
        data-testid="edit-candidate-sheet"
      >
        <SheetHeader>
          <SheetTitle>
            {t("candidates.detail.edit.title", {
              handle: candidate.tiktok_handle,
            })}
          </SheetTitle>
          <SheetDescription>
            {t("candidates.detail.edit.description")}
          </SheetDescription>
        </SheetHeader>

        <Form
          action={candidatePath(candidate.id)}
          method="patch"
          // Keep the Form mounted across the validation `redirect_back` so
          // the render-prop `errors` + typed values survive (POST/PATCH
          // `preserveState` default true). DO NOT set `preserveState: false`
          // — it would remount empty and drop the inline error (OKS-662).
          options={{ preserveScroll: true }}
          className="flex flex-col gap-4 px-4 pb-4"
          data-testid="edit-candidate-form"
          onSuccess={() => setOpen(false)}
        >
          {({ processing, errors }) => {
            submittingRef.current = processing
            const formErrors = errors as Record<
              string,
              string | string[] | undefined
            >
            return (
              <FormBody
                candidate={candidate}
                processing={processing}
                errors={formErrors}
                online={online}
                onCancel={() => setOpen(false)}
                t={t}
              />
            )
          }}
        </Form>
      </SheetContent>
    </Sheet>
  )
}

interface FormBodyProps {
  candidate: CandidateDetail
  processing: boolean
  errors: Record<string, string | string[] | undefined>
  online: boolean
  onCancel: () => void
  t: (key: string, opts?: Record<string, unknown>) => string
}

/**
 * Fields rendered inside the Inertia `<Form>` render-prop, prefilled from
 * the candidate. Split out so the disabled-on-{processing,offline} idiom
 * doesn't recur six times in a single scope.
 */
function FormBody({
  candidate,
  processing,
  errors,
  online,
  onCancel,
  t,
}: FormBodyProps) {
  const disabled = processing || !online
  // TikTok URL field prefills with the canonical handle (round-trips through
  // `normalize_handle` unchanged). See component docstring.
  const followers =
    candidate.approx_followers === null
      ? ""
      : String(candidate.approx_followers)

  return (
    <>
      {!online && (
        <Alert
          variant="default"
          data-testid="edit-candidate-offline-banner"
          role="status"
        >
          <AlertTitle>{t("candidates.bulk.errors.offline.title")}</AlertTitle>
          <AlertDescription>
            {t("candidates.detail.edit.offline_helper")}
          </AlertDescription>
        </Alert>
      )}

      <TextField
        id="candidate-edit-tiktok-url"
        name="candidate[tiktok_url]"
        label={t("candidates.add.fields.tiktok_url.label")}
        helper={t("candidates.add.fields.tiktok_url.helper")}
        required
        autoFocus
        defaultValue={candidate.tiktok_handle}
        disabled={disabled}
        error={pickError(errors.tiktok_url)}
        testIdInput="candidate-edit-input-tiktok-url"
        testIdError="candidate-edit-error-tiktok-url"
      />

      <TextField
        id="candidate-edit-display-name"
        name="candidate[display_name]"
        label={t("candidates.add.fields.display_name.label")}
        required
        defaultValue={candidate.display_name}
        disabled={disabled}
        error={pickError(errors.display_name)}
        testIdInput="candidate-edit-input-display-name"
        testIdError="candidate-edit-error-display-name"
      />

      <TextField
        id="candidate-edit-approx-followers"
        name="candidate[approx_followers]"
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        label={t("candidates.add.fields.approx_followers.label")}
        helper={t("candidates.add.fields.approx_followers.helper")}
        defaultValue={followers}
        disabled={disabled}
        error={pickError(errors.approx_followers)}
        testIdInput="candidate-edit-input-approx-followers"
        testIdError="candidate-edit-error-approx-followers"
      />

      <div className="flex flex-col gap-2">
        <Label htmlFor="candidate-edit-bio">
          {t("candidates.add.fields.bio.label")}
        </Label>
        <Textarea
          id="candidate-edit-bio"
          name="candidate[bio]"
          defaultValue={candidate.bio ?? ""}
          rows={3}
          disabled={disabled}
          aria-invalid={pickError(errors.bio) ? "true" : undefined}
          aria-describedby="candidate-edit-error-bio"
          data-testid="candidate-edit-input-bio"
        />
        <InputError
          id="candidate-edit-error-bio"
          data-testid="candidate-edit-error-bio"
          messages={toMessages(errors.bio)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-2">
          <Label htmlFor="candidate-edit-country">
            {t("candidates.add.fields.country.label")}
            <span className="text-destructive" aria-hidden="true">
              {" *"}
            </span>
          </Label>
          <OnboardingCountrySelect
            name="candidate[country]"
            id="candidate-edit-country"
            testId="candidate-edit-input-country"
            defaultValue={candidate.location.country ?? ""}
            required={false}
            disabled={disabled}
            errorMessages={toMessages(errors.country)}
            errorId="candidate-edit-error-country"
          />
          <InputError
            id="candidate-edit-error-country"
            data-testid="candidate-edit-error-country"
            messages={toMessages(errors.country)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="candidate-edit-city">
            {t("candidates.add.fields.city.label")}
          </Label>
          <Input
            id="candidate-edit-city"
            name="candidate[city]"
            type="text"
            maxLength={120}
            defaultValue={candidate.location.city ?? ""}
            disabled={disabled}
            aria-invalid={pickError(errors.city) ? "true" : undefined}
            aria-describedby="candidate-edit-error-city"
            data-testid="candidate-edit-input-city"
          />
          <InputError
            id="candidate-edit-error-city"
            data-testid="candidate-edit-error-city"
            messages={toMessages(errors.city)}
          />
        </div>
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={processing}
          aria-disabled={processing ? "true" : undefined}
          data-testid="edit-candidate-cancel"
          className="max-sm:h-11"
        >
          {t("candidates.detail.edit.cancel")}
        </Button>
        <Button
          type="submit"
          disabled={disabled}
          aria-disabled={disabled ? "true" : undefined}
          data-testid="edit-candidate-submit"
          className="max-sm:h-11"
        >
          {processing && (
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          )}
          {processing
            ? t("candidates.detail.edit.submitting")
            : t("candidates.detail.edit.submit")}
        </Button>
      </div>
    </>
  )
}

interface TextFieldProps {
  id: string
  name: string
  label: string
  helper?: string
  required?: boolean
  autoFocus?: boolean
  defaultValue?: string
  disabled?: boolean
  type?: string
  inputMode?: "text" | "numeric" | "email" | "url"
  min?: number
  step?: number
  error: string | null
  testIdInput: string
  testIdError: string
}

function TextField({
  id,
  name,
  label,
  helper,
  required,
  autoFocus,
  defaultValue,
  disabled,
  type = "text",
  inputMode,
  min,
  step,
  error,
  testIdInput,
  testIdError,
}: TextFieldProps) {
  const helperId = helper ? `${id}-helper` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [helperId, errorId].filter(Boolean).join(" ") || undefined
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>
        {label}
        {required && (
          <span className="text-destructive ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </Label>
      <Input
        id={id}
        name={name}
        type={type}
        inputMode={inputMode}
        min={min}
        step={step}
        defaultValue={defaultValue}
        // HTML5 `required` intentionally dropped so a blank submit reaches
        // the server and renders a localized inline error (mirrors the
        // add-Sheet + OKS-348/661 server-first posture).
        autoFocus={autoFocus}
        disabled={disabled}
        aria-required={required ? "true" : undefined}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy}
        data-testid={testIdInput}
      />
      {helper && (
        <p id={helperId} className="text-muted-foreground text-xs">
          {helper}
        </p>
      )}
      <InputError
        id={errorId}
        data-testid={testIdError}
        messages={error ? [error] : undefined}
      />
    </div>
  )
}

/**
 * Inertia render-prop `errors` is loosely typed — pull a single
 * string-or-array entry to the "first non-empty string" shape used by the
 * field-level `aria-invalid` toggles.
 */
function pickError(input: string | string[] | undefined): string | null {
  if (!input) return null
  if (Array.isArray(input)) return input.find(Boolean) ?? null
  return input || null
}
